import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import { Button } from "../components/ui/button";
import { Loader2, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import { Slider } from "../components/ui/slider";

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = new Image();
  image.crossOrigin = "anonymous";
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  const size = 400;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      "image/jpeg",
      0.9
    );
  });
}

export default function AvatarCropModal({ imageSrc, onSave, onCancel, saving }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
    onSave(blob);
  };

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (typeof document === "undefined") return null;

  const modal = (
    <div
      className="fixed inset-0 h-[100dvh] bg-black/80 flex flex-col overflow-hidden isolate"
      style={{ zIndex: 2147483647 }}
      data-testid="avatar-crop-modal"
    >
      <div
        className="flex items-center justify-between gap-3 bg-background border-b px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3"
        data-testid="avatar-modal-action-bar"
      >
        <Button
          variant="outline"
          className="h-10 rounded-xl px-4"
          onClick={onCancel}
          disabled={saving}
          data-testid="avatar-cancel-btn"
        >
          Cancel
        </Button>
        <p className="text-sm font-semibold text-foreground">Position photo</p>
        <Button
          className="h-10 rounded-xl px-4"
          onClick={handleSave}
          disabled={saving}
          data-testid="avatar-save-btn"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            "Save"
          )}
        </Button>
      </div>

      <div className="flex-1 relative min-h-0">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
        />
      </div>

      <div className="bg-background border-t px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] space-y-4">
        <div className="flex items-center gap-3 max-w-sm mx-auto">
          <ZoomOut className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.05}
            onValueChange={([v]) => setZoom(v)}
            data-testid="avatar-zoom-slider"
          />
          <ZoomIn className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={() => setRotation((r) => (r + 90) % 360)}
            data-testid="avatar-rotate-btn"
          >
            <RotateCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
